import { Button } from "@/components/ui/baseUi/button"
import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { cn } from "@/modules/lib/utils"
import { BoxOfferFormattedPrice } from "@modules/boxOffer/components/boxOfferFormSection"
import { BoxOfferInvitationEntity } from "@modules/boxOffer/entities/boxOfferInvitationEntity"
import { BoxOfferInvitationStatus, BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOffer, useBoxOfferInvitation } from "@modules/boxOffer/hooks/useBoxOffer"
import { getBoxOfferIcon, getOfferCommitment } from "@modules/boxOffer/utils/boxOfferUtils"
import {
  getFormattedCouponReduction,
  getPriceAfterDiscount,
} from "@modules/common/couponObject/utils/couponObjectUtils"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { capitalize } from "@modules/utils/stringUtils"
import dayjs from "dayjs"
import { motion } from "framer-motion"
import { ChevronDown, ChevronUp, Dot, Tag, Trash2 } from "lucide-react"
import { useState } from "react"

type Props = {
  item: BoxOfferInvitationEntity
  className?: string
} & React.ComponentProps<"div">

export function MemberOfferInvitationItem({ item, className, ...props }: Props) {
  const [isCollapsed, setIsCollapsed] = useState(true)

  const language = useLanguage()
  const confirmPopup = useConfirmPopup()
  const canEditMemberOffer = useBoxStaffPermission(BoxStaffPermissions.EDIT_MEMBER_OFFER)
  const { data: offer, isLoading } = useBoxOffer(item.boxOfferId)
  const { cancelBoxOfferInvitation, isCancelBoxOfferInvitation } = useBoxOfferInvitation(item.id, item.invitee.id)

  const bodyCancelInvitationPopup = (
    <div className="flex flex-col content-center items-center justify-center gap-4">
      <Text fontStyle="TL" className="flex text-textDefault-body">
        {word("box.invitation.popup.cancel.title")}
      </Text>
      <Text fontStyle="BM" className="flex w-88 text-center text-textDefault-caption">
        {word("box.invitation.popup.cancel.message")}
      </Text>
    </div>
  )

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev)
  }

  const handleCancel = () => {
    if (isCancelBoxOfferInvitation) return
    confirmPopup.show({
      classNames: {
        dialogContent: "w-104",
      },
      title: "",
      message: bodyCancelInvitationPopup,
      accept: () => {
        cancelBoxOfferInvitation()
          .then(() => {
            toast.success(word("box.invitation.popup.cancel.success"), {
              classNames: successToastClassName(),
            })
          })
          .catch((error) => {
            console.error(error)
            toast.error(word("box.invitation.popup.cancel.error"), {
              classNames: errorToastClassName(),
            })
          })
      },
      footerProps: {
        cancelText: word("global.cancel"),
        confirmText: word("global.continue"),
      },
    })
  }

  if (isLoading || !offer) return <Skeleton className="h-18 rounded-2xl" />

  return (
    <div
      className={cn(className, "mb-0 flex w-full flex-col gap-6 rounded-2xl bg-neutral-100 p-0 px-6 py-4")}
      key={item.id}
      {...props}
    >
      {/* HEADER */}
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <Text
              fontStyle="BM"
              className="w-40 max-w-40 wrap-break-word whitespace-break-spaces text-textDefault-body"
            >
              {offer.name}
            </Text>
            <Dot strokeWidth={10} color="currentColor" className={item.getColorStatus()} />
            <Text fontStyle="LS" className="wrap-break-word whitespace-break-spaces text-textDisabled">
              {item.getWordingStatus()}
            </Text>

            {item.deferredDate && (
              <div className="flex items-center gap-1 pl-3">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.member.table.offer.item.start")} ·
                </Text>
                <Text fontStyle="BS" className="text-textDefault-caption">
                  {dayjs(item.deferredDate).format("L")}
                </Text>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {isCollapsed ? (
              <ChevronDown size={25} onClick={toggleCollapse} className="cursor-pointer" />
            ) : (
              <ChevronUp size={25} onClick={toggleCollapse} className="cursor-pointer" />
            )}
          </div>
        </div>
      </div>
      {/* BODY EXPANDABLE */}
      <motion.div
        animate={{ height: isCollapsed ? 0 : "auto" }}
        className={`w-full flex-col gap-4 overflow-hidden ${isCollapsed ? "hidden" : "flex"}`}
      >
        {/* TYPE */}
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full items-baseline gap-2">
            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("box.member.table.offer.item.type")}
            </Text>
            <div className="flex grow border-t border-borderDefault" />
          </div>
          <div className="flex w-full items-center gap-2">
            {getBoxOfferIcon(offer.type)}
            <Text fontStyle="LL" className="text-textDefault-body">
              {word(`box.offer.form.types.${offer.type}`)}
            </Text>
          </div>
        </div>
        {/* STATUS */}
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full items-baseline gap-2">
            <Text fontStyle="BS" className="text-textDefault-caption">
              {word("box.member.table.offer.item.status.label")}
            </Text>
            <div className="flex grow border-t border-borderDefault" />
          </div>
          <div className="flex w-full flex-col gap-1">
            <Text fontStyle="LL" className="text-textDefault-body">
              {item.getWordingStatus()}
            </Text>

            <div className="flex items-center gap-1 pl-4">
              <Text fontStyle="LM" className="text-textDefault-body">
                {`${word("box.member.table.offer.itemInvitation.start")} ·`}
              </Text>
              <Text fontStyle="BS" className="text-textDefault-caption">
                {dayjs(item.createdAt).format("L")}
              </Text>
            </div>

            {item.deferredDate && (
              <div className="flex items-center gap-1 pl-4">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.member.table.offer.item.start")}
                </Text>
                <Text fontStyle="LM" className="text-textDefault-body">
                  ·
                </Text>
                <Text fontStyle="BS" className="text-textDefault-caption">
                  {dayjs(item.deferredDate).format("L")}
                </Text>
              </div>
            )}

            {item.subscriptionEndDate && (
              <div className="flex items-center gap-1 pl-4">
                <Text fontStyle="LM" className="text-textDefault-body">
                  {word("box.member.table.offer.item.end")}
                </Text>
                <Text fontStyle="LM" className="text-textDefault-body">
                  ·
                </Text>
                <Text fontStyle="BS" className="text-textDefault-caption">
                  {dayjs(item.subscriptionEndDate).format("L")}
                </Text>
              </div>
            )}

            {item.updatedAtDate &&
              (item.status === BoxOfferInvitationStatus.CANCELLED ||
                item.status === BoxOfferInvitationStatus.REFUSED) && (
                <div className="flex items-center gap-1 pl-4">
                  <Text fontStyle="LM" className="text-textDefault-body">
                    {item.status === BoxOfferInvitationStatus.CANCELLED
                      ? `${word("box.member.table.offer.item.cancelled")} ·`
                      : `${word("box.member.table.offer.item.refused")} ·`}
                  </Text>
                  <Text fontStyle="BS" className="text-textDefault-caption">
                    {dayjs(item.updatedAtDate).format("L")}
                  </Text>
                </div>
              )}

            <div className="flex items-start gap-1 pl-4">
              <Text fontStyle="LM" className="text-textDefault-body">
                {offer.type === BoxOfferType.Subscription || offer.type === BoxOfferType.Millenium
                  ? word("box.member.table.offer.item.commitmentPeriod")
                  : word("box.member.table.offer.item.validityPeriod")}
              </Text>
              <Text fontStyle="LM" className="text-textDefault-body">
                ·
              </Text>
              <div className="flex flex-col items-start gap-1">
                <Text fontStyle="LM" className="text-textDefault-caption">
                  {capitalize(getOfferCommitment(offer))}
                </Text>
                {offer.commitmentPeriodAutoRenewal && (
                  <Text fontStyle="LM" className="text-textDefault-caption">
                    {word("box.offer.form.infos.commitmentPeriodAutoRenewal.active")}
                  </Text>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* PRICE */}
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full items-baseline gap-2">
            <Text fontStyle="BS" className="font-semibold text-textDefault-caption">
              {word("box.member.table.offer.item.price")}
            </Text>
            <div className="flex grow border-t border-borderDefault" />
          </div>
          <div className="flex w-full items-center gap-1">
            {offer.price ? (
              <>
                <BoxOfferFormattedPrice
                  price={
                    {
                      value: item.getPriceAfterDicount(offer.price.value),
                      currency: offer.price.currency,
                    } as PriceObjectEntity
                  }
                  showRecurringPeriod={offer.type === BoxOfferType.Subscription}
                  recurringPeriod={offer.type === BoxOfferType.Subscription ? offer.recurringPeriod : undefined}
                  wholePartStyle="LL"
                  decimalPartStyle="LL"
                  periodStyle="LL"
                />
                {offer.price.value !== item.getPriceAfterDicount(offer.price.value) ? (
                  <BoxOfferFormattedPrice
                    price={offer.price}
                    showRecurringPeriod={offer.type === BoxOfferType.Subscription}
                    recurringPeriod={offer.type === BoxOfferType.Subscription ? offer.recurringPeriod : undefined}
                    wholePartStyle="LM"
                    decimalPartStyle="LM"
                    periodStyle="LM"
                    lineThrough={true}
                  />
                ) : null}
              </>
            ) : (
              <Text fontStyle="LL" className="text-textDefault-body">
                {word("box.member.table.offer.item.freePrice")}
              </Text>
            )}
          </div>
          <div className="flex w-full flex-col gap-4">
            {/* DISCOUNT */}
            {item.coupon && offer.price ? (
              <div className="flex w-full flex-col gap-2">
                <div className="flex items-center gap-1 pl-4">
                  <Tag size={15} color="currentColor" className="text-textDisabled" />
                  <Text fontStyle="LS" className="font-semibold text-textDisabled">
                    {word("box.member.table.offer.item.discount.label")}
                  </Text>
                </div>
                <div className="flex flex-col gap-1 pl-4">
                  <Text fontStyle="BS" className="text-textDefault-body">
                    {getFormattedCouponReduction(item.coupon.type, item.coupon.value, language, offer.price.currency)} -{" "}
                    {formatCurrency(
                      getPriceAfterDiscount(item.coupon, offer.price.value),
                      offer.price.currency,
                      language,
                    )}
                  </Text>
                  <Text fontStyle="LS" className="text-textDefault-caption">
                    {item.getWordingCouponDate(offer)}
                  </Text>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {item.status === BoxOfferInvitationStatus.PENDING && canEditMemberOffer ? (
          <div className="flex w-full flex-row-reverse">
            <Button onClick={handleCancel} variant="outline" size="iconMd">
              <Trash2 size={16} className="text-textDefault-body" />
            </Button>
          </div>
        ) : null}
      </motion.div>
    </div>
  )
}
